<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import * as Sentry from "@sentry/vue";

import {
  useHotspotBreakdown,
  MATERIAL_TOTAL_EXCLUDED_STAGES,
  STAGE_MODULES,
  findProjectMaterialRow,
  sumMaterialTotal,
} from "@/features/Dashboard/composables/useHotspotBreakdown";
import { ALL_STAGES_SUMMARY } from "@/features/Dashboard/stageFilterSummary";
import { resolveProjectSeriesAvailability } from "./projectSeriesAvailability";
import { useEnum } from "@/stores/project/project.enum";
import { declaredUnitSymbol } from "@/utils/declaredUnitSymbol";
import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";
import type { Material } from "@/types/Views/MaterialDatabase/Material";
import { formatNumber } from "@/services/Utils/DecimalFormatter";
import { shortenIndicatorUnit, sumEpdStagesPerDeclaredUnit } from "@/services/Utils/materialEmissions";
import { getDeclaredEpdSource } from "./declaredEpdSource";
import { VerticalBarChartNew } from "@/features/Charts";
import SwitchTabs from "@/components/Reusables/Switches/SwitchTabs.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";

// Emissions breakdown for one mapped material, one column per life-cycle MODULE (finer-grained than the
// dashboard chart, which buckets B1-B7/C1-C4 into single B/C columns). Toggles between Project level (quantity-scaled) and EPD level (the mapped EPD's own declared values).
const props = defineProps<{
  material: SpeckleMaterial;
  includedPhases: number[];
  indicatorType?: { id: number; identifier?: string | null };
  // Deliberately unused on the EPD-level series: scaling B4 to the consideration period needs the API's
  // replacement-count math (CalculateAndReplaceB4), not mirrored here. The project-level series already arrives scaled.
  considerationPeriod?: number | null;
  unit: string;
}>();

const { getProjectMaterialRows, scopeToSelectedStages, stageScopeSummary } = useHotspotBreakdown();
const { declaredUnits } = storeToRefs(useEnum());

const containsStandardValues = computed(() =>
  (props.material.mapping?.lciaResults ?? []).some(
    (lcia) => lcia.indicators?.some((indicator) => indicator.type === props.indicatorType?.id && indicator.source === 1)
  )
);

// EPD level: material.mapping.lciaResults CANNOT be used — the API overwrites it with the project-scaled
// contribution, so it's already divided by the calculation-type denominator. The EPD service returns unscaled values by construction; constructions have their own endpoint for aggregated lciaResults.
const epdSource = ref<Material | null>(null);
const isLoadingEpd = ref<boolean>(false);
// Guards against an earlier request landing after a later one and overwriting it (and against the
// earlier one's `finally` clearing the loading flag while the later one is still in flight).
let latestEpdRequest = 0;

// Routing lives in getDeclaredEpdSource (shared with the modal's Inspect view). It reports its own
// failures to Sentry and resolves null rather than throwing, so epdSource never falls back to the project-scaled mapping payload; the catch below is a backstop.
const loadEpdSource = async (mapping: Material | null | undefined) => {
  const request = ++latestEpdRequest;
  epdSource.value = null;
  if (!mapping?.id) {
    isLoadingEpd.value = false;
    return;
  }
  isLoadingEpd.value = true;
  try {
    const source = await getDeclaredEpdSource(mapping);
    if (request !== latestEpdRequest) return;
    epdSource.value = source;
  } catch (error: unknown) {
    if (request !== latestEpdRequest) return;
    Sentry.captureException(error);
  } finally {
    if (request === latestEpdRequest) isLoadingEpd.value = false;
  }
};

// Keyed on the id and the construction flag — the two things that decide which record to read — but
// the fetch is handed the mapping itself, since the routing also looks at `constructionEpds`.
const stopEpdWatch = watch(
  () => [props.material.mapping?.id, props.material.mapping?.isConstruction] as const,
  () => loadEpdSource(props.material.mapping),
  { immediate: true }
);
onUnmounted(() => stopEpdWatch());

const epdStages = computed(() =>
  sumEpdStagesPerDeclaredUnit(epdSource.value?.lciaResults, {
    indicatorType: props.indicatorType?.id,
    includedPhases: props.includedPhases,
    declaredValue: epdSource.value?.declaredUnit?.declaredValue,
  })
);

// Project level: rows keyed by project material id (fallback: identifier) via the shared findProjectMaterialRow
// (also used by the Total GWP KPI, so the two can't disagree), read from the UNFILTERED breakdown — so the Stages filter must be applied here by hand, on raw stage keys, not the dashboard's collapsed buckets.
const projectRows = computed(() => getProjectMaterialRows());
const projectRow = computed(() => findProjectMaterialRow(projectRows.value, props.material.id, props.material.identifier));
const projectRawStages = computed<Record<string, number>>(() => scopeToSelectedStages(projectRow.value?.rawStages));

// Why the Project series is or isn't showable, and the sentence that says so. Replaces the silent
// fall-through to EPD numbers that used to happen whenever the project side didn't resolve.
const projectSeries = computed(() =>
  resolveProjectSeriesAvailability({
    row: projectRow.value,
    breakdownHasRows: projectRows.value.length > 0,
    includeMapping: props.material.includeMapping !== false,
  })
);
const projectAvailable = computed(() => projectSeries.value.available);

// Defaults to Project when resolvable, else EPD-only — the switch stays on screen either way (disabled,
// with projectSeries.reason beside it) so EPD numbers are never presented as though they were the project view.
const showEpd = ref(false);
const mode = computed<"project" | "epd">(() => (!projectAvailable.value || showEpd.value ? "epd" : "project"));

// EPD-mode states. The mapping payload is never used as a stand-in while the EPD is in flight or
// after it failed — those numbers are on the project's basis, which is the bug this panel had.
const isEpdPending = computed(() => mode.value === "epd" && isLoadingEpd.value);
const isEpdUnavailable = computed(() => mode.value === "epd" && !isLoadingEpd.value && !epdSource.value);

// Total is summed from this raw map, not the plotted columns, because B6 must be excluded from it — the
// same map the chart reads module-by-module (no bucketing step folds B6 into a "B" column).
const activeRawStages = computed<Record<string, number>>(() =>
  mode.value === "project" ? projectRawStages.value : epdStages.value.stageValues
);

// EPD view takes its emission base from the indicator's own `unit` field rather than splicing the
// dashboard label, so the axis is a property of the plotted values, not an assumption; the dashboard base is only a fallback for EPDs with no unit on the indicator.
const emissionBase = computed(() => props.unit.split("/")[0].trim());
const epdDeclaredSymbol = computed(() => {
  const declared = epdSource.value?.declaredUnit ?? props.material.mapping?.declaredUnit;
  if (!declared) return "";
  return (
    declaredUnitSymbol(declaredUnits.value[declared.declaredUnit]) ||
    (declared.epdDeclaredUnitType ? declaredUnitSymbol(declaredUnits.value[declared.epdDeclaredUnitType]) : "")
  );
});
const epdEmissionBase = computed(() => (epdStages.value.unit ? shortenIndicatorUnit(epdStages.value.unit) : emissionBase.value));
const epdUnit = computed(() =>
  epdDeclaredSymbol.value ? `${epdEmissionBase.value}/${epdDeclaredSymbol.value}` : epdEmissionBase.value
);
const activeUnit = computed(() => (mode.value === "project" ? props.unit : epdUnit.value));

// Shown on every EPD view, not only when the Project view is missing — "project totals aren't available"
// has its own line next to the disabled switch instead, where it explains why.
const epdCaption = computed(() => {
  const per = epdDeclaredSymbol.value ? `per ${epdDeclaredSymbol.value}` : "per declared unit";
  return `Showing the mapped EPD's own declared values ${per} — no project quantity, area or consideration period applied.`;
});

// The backend excludes B6 and D from a material's total (DashboardConstants.ExcludedStagesForMaterialTotal),
// so this sums off the raw stage map, not the plotted columns — B6 is its own column now, so it must be subtracted explicitly rather than folded away by bucketing.
const total = computed(() => sumMaterialTotal(activeRawStages.value));

// Whether the note under the Total is warranted: only when a stage that isn't counted actually
// carries a value, so the note doesn't explain a discrepancy nobody can see.
const excludedStagesShown = computed(() =>
  MATERIAL_TOTAL_EXCLUDED_STAGES.some((stage) => (activeRawStages.value[stage] ?? 0) !== 0)
);
// Built from the exported list rather than spelled out, so the copy can't drift from what is excluded.
const excludedStagesNote = `${MATERIAL_TOTAL_EXCLUDED_STAGES.join(" and ")} are shown as columns but not counted in the Total.`;

// Names the Stages filter when it narrows the Project series, so a smaller figure reads as a scope, not
// a wrong number. Null on the EPD series (untouched by this filter, scoped only by includedPhases) and when nothing is narrowed.
const stageScopeNote = computed<string | null>(() => {
  if (mode.value !== "project" || stageScopeSummary.value === ALL_STAGES_SUMMARY) return null;
  return `Scoped to the Stages filter: ${stageScopeSummary.value}.`;
});

// The Project series can be empty because the filter left nothing, not because the material has nothing —
// resolveProjectSeriesAvailability reads the row UNSCOPED, so the panel stays on Project instead of flipping to EPD numbers.
const isEmptyByStageScope = computed(() => mode.value === "project" && stageScopeNote.value !== null);

// One series of MODULE columns (not the dashboard's coarser stage buckets). Modules worth exactly 0 are
// dropped so the axis isn't padded with empty ones; negatives are kept — a module D credit belongs below the zero line.
const chartData = computed(() => ({
  stages: {
    name: "By life-cycle module",
    entries: STAGE_MODULES.map((module) => ({
      id: module.key,
      isDefaultValue: false,
      label: module.label,
      value: activeRawStages.value[module.key] ?? 0,
    })).filter((entry) => entry.value !== 0),
  },
}));

// Presence of stage data, not a positive total: a material whose only stage is a negative credit still
// has something to plot, and `total > 0` used to render it as "no emission data".
const hasData = computed(() => chartData.value.stages.entries.length > 0);

// VerticalBarChartNew redraws on `data` changes on its own, but this key is kept so a Project/EPD mode
// switch (or a dashboard refresh mid-modal) starts from a clean chart instead of transitioning between two unrelated series.
const chartKey = computed(
  () =>
    `${mode.value}|${activeUnit.value}|${chartData.value.stages.entries.map((entry) => `${entry.id}=${entry.value}`).join(",")}`
);
</script>

<template>
  <div>
    <!-- pr-px-2 keeps the switch's outset ring off the tab panel's overflow clip edge (the panel is
         overflow-y-auto, which forces overflow-x to auto), so the switch isn't cut off on the right. -->
    <div class="pr-px-2 mb-3 flex items-start justify-between gap-3">
      <p class="t-footnote text-type-muted mt-px-8 tracking-wide uppercase">By life-cycle module</p>
      <!-- Always rendered, even when the project side can't be resolved — hiding it let EPD numbers stand
           in for the project view unannounced. Disabled with the reason underneath instead. -->
      <div class="flex shrink-0 flex-col items-end gap-1">
        <SwitchTabs
          v-model:value="showEpd"
          size="sm"
          :disabled="!projectAvailable">
          <template #left>Project</template>
          <template #right>EPD</template>
        </SwitchTabs>
        <p
          v-if="!projectAvailable"
          class="t-xs text-type-muted max-w-64 text-right">
          {{ projectSeries.reason }}
        </p>
      </div>
    </div>

    <!-- The EPD is fetched from the EPD service, so the declared-unit view has a loading state. The
         mapping payload is deliberately not shown in the meantime — it is on the project's basis. -->
    <div
      v-if="isEpdPending"
      class="flex h-64 w-full items-center justify-center">
      <LoadingSpinner
        size="md"
        fill="primary" />
    </div>

    <p
      v-else-if="isEpdUnavailable"
      class="t-small text-type-muted">
      Couldn't load the mapped EPD's declared values. Try again in a moment.
    </p>

    <template v-else-if="hasData">
      <!-- Explicit height required: the chart renders into an absolutely positioned canvas inside an h-full
           wrapper, so it needs a sized parent to measure. 16rem matches the dashboard figure's proportions. -->
      <div class="h-64 w-full">
        <VerticalBarChartNew
          :key="chartKey"
          :data="chartData"
          :unit="activeUnit" />
      </div>

      <!-- Total: KPI band styled to match the new design language -->
      <div class="mt-px-16 rounded-8 p-px-12 bg-slate-100">
        <p class="t-footnote text-type-muted tracking-wide uppercase">Total</p>
        <p class="t-sh4 mt-px-2 text-type-default">
          {{ formatNumber(total) }}
          <span class="t-caption text-type-muted">{{ activeUnit }}</span>
        </p>
        <!-- The columns visibly stop adding up to the printed figure otherwise. -->
        <p
          v-if="excludedStagesShown"
          class="t-xs text-type-muted mt-px-4">
          {{ excludedStagesNote }}
        </p>
        <!-- Beside the Total rather than above the chart: it is the figure most likely to be read
             against a number from somewhere else, and the one the scope silently changes. -->
        <p
          v-if="stageScopeNote"
          class="t-xs text-type-muted mt-px-4">
          {{ stageScopeNote }}
        </p>
      </div>

      <p
        v-if="mode === 'epd'"
        class="t-xs text-type-muted mt-px-8">
        {{ epdCaption }}
      </p>

      <p
        v-if="mode === 'epd' && epdStages.hasReplacementStage"
        class="t-xs text-type-muted mt-px-8">
        B4 is the EPD's declared replacement value — the number of replacements over the project's consideration period is applied
        in the Project view.
      </p>

      <p
        v-if="containsStandardValues"
        class="t-xs text-type-muted mt-px-8">
        * Uses standard values
      </p>
    </template>

    <!-- A narrow Stages filter reads as a broken mapping otherwise. -->
    <p
      v-else-if="isEmptyByStageScope"
      class="t-small text-type-muted">
      No emission data for the stages the Stages filter selects ({{ stageScopeSummary }}).
    </p>

    <p
      v-else
      class="t-small text-type-muted">
      No emission data available for this material.
    </p>
  </div>
</template>
